/**
 * Selection Sort Visualizer
 */

import type {
  Visualizer,
  VisualizerConfig,
  Snapshot,
  Step,
  ActionPayload,
  ComplexityInfo,
  ElementState,
  InputField,
  ActionButton,
  CodeSnippets,
} from '../core/types';
import { createStepMeta } from '../core/types';
import { registry } from '../core/registry';
import type { SortingData } from './sorting-shared';
import { drawArrayBars, generateRandomArray, STATE_COLORS } from './sorting-shared';

export function generateSelectionSortSteps(arr: number[]): Step<SortingData>[] {
  const steps: Step<SortingData>[] = [];
  const elements = arr.map((value) => ({ value, state: 'default' as ElementState }));
  let stepId = 0;
  let comparisons = 0;
  let swaps = 0;

  steps.push({
    id: stepId++,
    description: 'Initial array state',
    snapshot: { data: { elements: elements.map((e) => ({ ...e })) } },
    meta: createStepMeta({
      reads: 0,
      writes: 0,
      highlightedLine: 1,
      highlightColor: STATE_COLORS.default,
    }),
  });

  const n = elements.length;

  for (let i = 0; i < n - 1; i++) {
    let minIdx = i;

    // Mark current position as active
    const startElements = elements.map((e, idx) => ({
      ...e,
      state: (idx < i ? 'sorted' : idx === i ? 'active' : 'default') as ElementState,
    }));
    steps.push({
      id: stepId++,
      description: `Finding minimum element from index ${i} to ${n - 1}`,
      snapshot: { data: { elements: startElements } },
      meta: createStepMeta({
        comparisons,
        swaps,
        reads: comparisons * 2,
        writes: swaps * 2,
        highlightedLine: 2,
        highlightColor: STATE_COLORS.active,
      }),
    });

    for (let j = i + 1; j < n; j++) {
      comparisons++;
      const comparingElements = elements.map((e, idx) => ({
        ...e,
        state: (idx < i
          ? 'sorted'
          : idx === minIdx
            ? 'pivot'
            : idx === j
              ? 'comparing'
              : 'default') as ElementState,
      }));
      steps.push({
        id: stepId++,
        description: `Comparing element at ${j} (${elements[j].value}) with current min at ${minIdx} (${elements[minIdx].value})`,
        snapshot: { data: { elements: comparingElements } },
        meta: createStepMeta({
          comparisons,
          swaps,
          reads: comparisons * 2,
          writes: swaps * 2,
          highlightedLine: 3,
          highlightColor: STATE_COLORS.comparing,
        }),
        activeIndices: [minIdx, j],
      });

      if (elements[j].value < elements[minIdx].value) {
        minIdx = j;
        const newMinElements = elements.map((e, idx) => ({
          ...e,
          state: (idx < i ? 'sorted' : idx === minIdx ? 'pivot' : 'default') as ElementState,
        }));
        steps.push({
          id: stepId++,
          description: `New minimum found: ${elements[minIdx].value} at index ${minIdx}`,
          snapshot: { data: { elements: newMinElements } },
          meta: createStepMeta({
            comparisons,
            swaps,
            reads: comparisons * 2,
            writes: swaps * 2,
            highlightedLine: 4,
            highlightColor: STATE_COLORS.pivot,
          }),
        });
      }
    }

    if (minIdx !== i) {
      swaps++;
      const swappingElements = elements.map((e, idx) => ({
        ...e,
        state: (idx < i
          ? 'sorted'
          : idx === i || idx === minIdx
            ? 'swapping'
            : 'default') as ElementState,
      }));
      steps.push({
        id: stepId++,
        description: `Swapping ${elements[i].value} at index ${i} with ${elements[minIdx].value} at index ${minIdx}`,
        snapshot: { data: { elements: swappingElements } },
        meta: createStepMeta({
          comparisons,
          swaps,
          reads: comparisons * 2,
          writes: swaps * 2,
          highlightedLine: 5,
          highlightColor: STATE_COLORS.swapping,
        }),
        modifiedIndices: [i, minIdx],
      });

      const temp = elements[i];
      elements[i] = elements[minIdx];
      elements[minIdx] = temp;
    }

    elements[i].state = 'sorted';
  }

  if (elements.length > 0) {
    elements[n - 1].state = 'sorted';
  }
  steps.push({
    id: stepId++,
    description: 'Array is now fully sorted!',
    snapshot: {
      data: { elements: elements.map((e) => ({ ...e, state: 'sorted' as ElementState })) },
    },
    meta: createStepMeta({
      comparisons,
      swaps,
      reads: comparisons * 2,
      writes: swaps * 2,
      highlightedLine: 6,
      highlightColor: STATE_COLORS.sorted,
    }),
  });

  return steps;
}

class SelectionSortVisualizer implements Visualizer<SortingData> {
  readonly config: VisualizerConfig = {
    id: 'selection-sort',
    name: 'Selection Sort',
    category: 'sorting',
    description: 'Finds the minimum element and places it at the beginning, repeatedly.',
    defaultSpeed: 300,
  };

  private arraySize = 15;
  private currentData: SortingData;

  constructor() {
    this.currentData = generateRandomArray(this.arraySize);
  }

  getInitialState(): Snapshot<SortingData> {
    this.currentData = generateRandomArray(this.arraySize);
    return { data: this.currentData };
  }

  getSteps(actionPayload: ActionPayload<SortingData>): Step<SortingData>[] {
    if (actionPayload.type === 'randomize') {
      this.arraySize = (actionPayload.params?.size as number) || this.arraySize;
      this.currentData = generateRandomArray(this.arraySize);
      return [
        {
          id: 0,
          description: 'Generated new random array',
          snapshot: { data: this.currentData },
          meta: createStepMeta({ reads: 0, writes: 0, highlightedLine: 1 }),
        },
      ];
    }
    const arr =
      actionPayload.data?.elements.map((e) => e.value) ??
      this.currentData.elements.map((e) => e.value);
    return generateSelectionSortSteps(arr);
  }

  draw(snapshot: Snapshot<SortingData>, ctx: CanvasRenderingContext2D): void {
    const canvas = ctx.canvas;
    const dpr = window.devicePixelRatio || 1;
    drawArrayBars(snapshot.data.elements, ctx, canvas.width / dpr, canvas.height / dpr);
  }

  getPseudocode(): string[] {
    return [
      'for (let i = 0; i < n - 1; i++) {',
      '  let minIdx = i;',
      '  for (let j = i + 1; j < n; j++) {',
      '    if (arr[j] < arr[minIdx]) minIdx = j;',
      '  }',
      '  [arr[i], arr[minIdx]] = [arr[minIdx], arr[i]];',
      '}',
    ];
  }

  getCode(): CodeSnippets {
    return {
      typescript: [
        'function selectionSort(arr: number[]): void {',
        '  const n = arr.length;',
        '  for (let i = 0; i < n - 1; i++) {',
        '    let minIdx = i;',
        '    for (let j = i + 1; j < n; j++) {',
        '      if (arr[j] < arr[minIdx]) minIdx = j;',
        '    }',
        '    [arr[i], arr[minIdx]] = [arr[minIdx], arr[i]];',
        '  }',
        '}',
      ],
      python: [
        'def selection_sort(arr: list[int]) -> None:',
        '    n = len(arr)',
        '    for i in range(n - 1):',
        '        min_idx = i',
        '        for j in range(i + 1, n):',
        '            if arr[j] < arr[min_idx]:',
        '                min_idx = j',
        '        arr[i], arr[min_idx] = arr[min_idx], arr[i]',
      ],
      java: [
        'void selectionSort(int[] arr) {',
        '    int n = arr.length;',
        '    for (int i = 0; i < n - 1; i++) {',
        '        int minIdx = i;',
        '        for (int j = i + 1; j < n; j++) {',
        '            if (arr[j] < arr[minIdx]) minIdx = j;',
        '        }',
        '        int temp = arr[i];',
        '        arr[i] = arr[minIdx];',
        '        arr[minIdx] = temp;',
        '    }',
        '}',
      ],
    };
  }

  getComplexity(): ComplexityInfo {
    return { time: { best: 'O(n²)', average: 'O(n²)', worst: 'O(n²)' }, space: 'O(1)' };
  }

  getInputs(): InputField[] {
    return [
      {
        id: 'size',
        label: 'Array Size',
        type: 'range',
        defaultValue: 15,
        min: 5,
        max: 50,
        step: 1,
      },
    ];
  }

  getActions(): ActionButton[] {
    return [
      { id: 'randomize', label: 'Randomize', primary: false },
      { id: 'sort', label: 'Sort', primary: true },
    ];
  }
}

registry.register<SortingData>(
  {
    id: 'selection-sort',
    name: 'Selection Sort',
    category: 'sorting',
    description:
      'Finds the minimum element and swaps it to the front, building a sorted portion left to right.',
    defaultSpeed: 300,
  },
  () => new SelectionSortVisualizer()
);

export { SelectionSortVisualizer };
